import axios from "axios";

const API = axios.create({
    // baseURL: "http://127.0.0.1:8000"
    // baseURL: "https://test.api.mealno.in"
    baseURL: "https://api.mealno.in"
});

// 52🔐 Attach Access Token automatically
API.interceptors.request.use((req) => {
    const token = localStorage.getItem("admin_access_token");

    if (token) {
        req.headers.Authorization = `Bearer ${token}`;
    }

    return req;
});

// 🔄 Queue helpers to prevent race conditions during concurrent refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// 🔄 Auto-Refresh Response Interceptor
API.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Catch 401 token expiration windows
        if (error.response?.status === 401 && !originalRequest._retry) {

            // Stop loops if the crash occurs on auth endpoints themselves
            if (originalRequest.url === '/auth/refresh' || originalRequest.url === '/admin/login') {
                return Promise.reject(error);
            }

            // 👇 NEW: If a refresh is already happening, queue this request
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return API(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem("admin_refresh_token");

                if (!refreshToken) {
                    throw new Error("No admin refresh token stored.");
                }

                // Hit the backend with basic axios to bypass request loops
                const res = await axios.post(`${API.defaults.baseURL}/auth/refresh`, {
                    refresh_token: refreshToken
                });

                const newAccessToken = res.data.access_token;

                // Save fresh access token credentials
                localStorage.setItem("admin_access_token", newAccessToken);

                // 👇 NEW: Release the queue with the new token
                isRefreshing = false;
                processQueue(null, newAccessToken);

                // Update configuration authorization and execute original call
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return API(originalRequest);

            } catch (refreshError) {
                isRefreshing = false;

                const status = refreshError.response?.status;

                // Only treat this as "refresh token is actually dead" on a real 401
                // from /auth/refresh. Network errors, timeouts, 5xx, 429 are transient —
                // queued requests just fail this once, don't wipe a still-valid session.
                if (status === 401) {
                    processQueue(refreshError, null);

                    localStorage.removeItem("admin_access_token");
                    localStorage.removeItem("admin_refresh_token");
                    localStorage.removeItem("admin");

                    window.location.href = "/login";
                } else {
                    processQueue(refreshError, null);
                }

                return Promise.reject(refreshError);
            }

        }

        return Promise.reject(error);
    }
);

// Admin Authentication/Management Endpoints
export const loginAdmin = (data) => API.post("/admin/login", data);
export const logoutAdmin = () => {
    localStorage.removeItem("admin_access_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin");

    window.location.href = "/login";
};
export const getVendors = (params) => API.get("/admin/vendors", { params });
export const createVendor = (data) => API.post("/vendors", data);
export const updateVendor = (id, data) => API.put(`/vendors/${id}`, data);
export const deleteVendor = (id) => API.delete(`/vendors/${id}`);
export const createZonalAdmin = (data) => API.post("/admin/create-zonal-admin", data);
export const getZonalAdmins = () => API.get("/admin/zonal-admins");
export const getVendorById = (id) => API.get(`/vendor/${id}`);
export const createMenuItem = (data) => API.post("/menu-item", data);
export const assignMenuToVendor = (vendorId, data) => API.post(`/vendor/${vendorId}/menu`, data);
export const getVendorMenu = (vendorId) => API.get(`/vendor/${vendorId}/menu`);
export const toggleMenu = (menuId) => API.put(`/vendor/menu/${menuId}`);
export const getVendorOrders = (vendorId) => API.get(`/vendor/${vendorId}/orders`);
export const getOrderDetails = (orderId) => API.get(`/admin/order/${orderId}`);
export const getVendorFinancial = (vendorId) => API.get(`/vendor/${vendorId}/financial-summary`);
export const updateMenuItem = (id, data) => API.put(`/menu-item/${id}`, data);
export const deleteMenuItem = (id) => API.delete(`/menu-item/${id}`);
export const createDeliveryBoy = (data) => API.post("/admin/create-delivery-boy", data);
export const getDeliveryBoys = (params) => API.get("/admin/delivery-boys", { params });
export const updateDeliveryBoy = (id, data) => API.put(`/admin/delivery-boy/${id}`, data);
export const deleteDeliveryBoy = (id) => API.delete(`/admin/delivery-boy/${id}`);

// 🔥 BATCH APIs
export const getBatches = () => API.get("/admin/batches");
export const getBatchDetails = (id) => API.get(`/admin/batch/${id}`);
export const assignBatch = (data) => API.post("/admin/batch/assign", data);

// 🔥 SINGLE & LIVE ORDER APIs
export const getSingleOrders = () => API.get("/admin/single-orders");
export const assignSingleOrder = (data) => API.post("/admin/order/assign", data);
export const getLiveOrders = () => API.get("/admin/live-orders");
export const markOrderReady = (orderId) => API.put(`/admin/order/${orderId}/ready`);
export const cancelOrder = (data) => API.put("/admin/order/cancel", data);


// 🔥 ALL ORDERS API (NEW)
export const getAllOrders = (skip = 0, limit = 20) => API.get(`/admin/all-orders?skip=${skip}&limit=${limit}`);

// 🔥 COUPONS
export const createCoupon = (data, params) => API.post("/admin/coupons", data, { params });
export const getCoupons = (params) => API.get("/admin/coupons", { params });
export const updateCoupon = (id, data) => API.put(`/admin/coupon/${id}`, data);
export const toggleCouponStatus = (id) => API.put(`/admin/coupon/${id}/status`);
export const deleteCoupon = (id) => API.delete(`/admin/coupon/${id}`);

// 🔥 FOOD TAGS (NEW)
export const getFoodTags = (params) => API.get("/admin/food-tags", { params });
export const createFoodTag = (data) => API.post("/admin/food-tags", data);
export const toggleFoodTag = (id) => API.put(`/admin/food-tags/${id}/toggle`);
export const deleteFoodTag = (id) => API.delete(`/admin/food-tags/${id}`);
export const updateZonalAdmin = (id, data) => API.put(`/admin/zonal-admin/${id}`, data);
export const deleteZonalAdmin = (id) => API.delete(`/admin/zonal-admin/${id}`);
export const getVendorFinancialSummary = (vendorId, params) => API.get(`/vendor/${vendorId}/financial-summary`, { params });
export const getPlatformFinancials = (params) => API.get(`/admin/financial-summary`, { params });
export const getRiderSummary = (boyId, params) => API.get(`/admin/delivery-boy/${boyId}/summary`, { params });

export default API;
